import loadable from '@loadable/component';
import { AspectRatioType } from '@models/IAspectRatio';
import { IVideoAttributes } from '@models/IVideoAttributes';
import { IVideoSubtitle } from '@models/IVideoSubtitle';
import updateDataLayer from '@utils/helpers/gtm';
import useIsClientSide from '@utils/hooks/useIsClientSide';
import classNames from 'classnames';
import { MotionValue, motion } from 'framer-motion';
import { ChangeEvent, ForwardedRef, forwardRef, useRef } from 'react';
import { Config } from 'react-player';

const ReactPlayer = loadable(
  () => import(/* webpackChunkName: "react-player" */ 'react-player')
);

interface Props {
  readonly classes?: string;
  readonly config?: Config;
  readonly controls?: boolean;
  readonly epiEdit?: string;
  readonly inlineStyles?: React.CSSProperties;
  readonly isPlaying?: boolean;
  readonly subtitles?: IVideoSubtitle[];
  readonly title?: string;
  readonly url?: string;
  readonly videoAttributes?: IVideoAttributes;
  readonly videoRatio?: AspectRatioType;
  readonly videoSource?: string;
  readonly videoWrapperOpacityProgress?: MotionValue<number>;
  readonly videoWidthStyle?: string;
  readonly onEndedVideo?: () => void;
  readonly onVideoPlay?: () => void;
  readonly onVideoPause?: () => void;
  readonly onVideoReady?: () => void;
  readonly onVideoStart?: () => void;
  readonly onVolumeChange?: (videoElement: HTMLVideoElement) => void;
}

const Video = forwardRef(
  (
    {
      classes,
      config,
      controls = true,
      epiEdit,
      inlineStyles,
      isPlaying,
      title,
      subtitles,
      videoAttributes,
      videoRatio = 'aspect-video',
      videoSource,
      videoWidthStyle = '100%',
      videoWrapperOpacityProgress,
      onEndedVideo,
      onVideoPause,
      onVideoPlay,
      onVideoReady,
      onVideoStart,
      onVolumeChange,
    }: Props,
    ref
  ) => {
    const isClientSide = useIsClientSide();
    const prevProgress = useRef(0);
    const isVideoComplete = useRef(false);

    const handleVideoProgressEvent = (progress: string) => {
      updateDataLayer({
        event: 'videoProgress',
        video_progress: progress,
        video_title: title || videoSource,
      });
    };

    const handleVideoProgress = (progress: number) => {
      if (isVideoComplete.current) return;

      if (prevProgress.current < 0.25 && progress > 0.25) {
        handleVideoProgressEvent('25%');
      }
      if (prevProgress.current < 0.5 && progress > 0.5) {
        handleVideoProgressEvent('50%');
      }
      if (prevProgress.current < 0.75 && progress > 0.75) {
        handleVideoProgressEvent('75%');
      }
      if (prevProgress.current > 0.9 && progress < 0.1) {
        handleVideoProgressEvent('100%');
        isVideoComplete.current = true;
      }

      prevProgress.current = progress;
    };

    if (!videoSource) return null;

    return (
      <motion.div
        ref={ref as ForwardedRef<HTMLDivElement>}
        className={classNames(classes, videoRatio, 'w-full')}
        data-epi-property-name={epiEdit}
        data-testid={isPlaying ? 'video-playing' : 'video-paused'}
        style={{
          opacity: videoWrapperOpacityProgress ?? 1,
          ...inlineStyles,
        }}
      >
        {isClientSide && (
          <ReactPlayer
            url={videoSource}
            width={videoWidthStyle}
            height="100%"
            controls={controls}
            playing
            onProgress={({ played }) => {
              handleVideoProgress(played);
            }}
            onStart={() => {
              onVideoStart?.();
            }}
            onEnded={() => {
              onEndedVideo?.();
            }}
            onPause={onVideoPause}
            onPlay={onVideoPlay}
            onReady={onVideoReady}
            config={{
              ...config,
              file: {
                tracks: subtitles || [],
              },
            }}
            onVolumeChange={(event: ChangeEvent<HTMLVideoElement>) => {
              onVolumeChange?.(event.target);
            }}
            {...videoAttributes}
          />
        )}
      </motion.div>
    );
  }
);

export default Video;
